import classNames from 'classnames';
import React, {ReactElement, ReactNode, VideoHTMLAttributes} from 'react';
import ElementLoadingSkeleton from '../loadingSkeleton/ElementLoadingSkeleton';
import {useMediaLoad} from './useMediaLoad';
import './media.scss';

interface MediaVideoProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src' | 'onError' | 'onLoadedData'> {
    src?: string;
    type?: string;
    fallbackSrc?: string;
    unsupportedText: ReactNode;
    maxRetries?: number;
    wrapperClassName?: string;
}

function MediaVideo(props: MediaVideoProps): ReactElement {
    const {src, type = 'video/mp4', fallbackSrc, unsupportedText, maxRetries, wrapperClassName, className, style, ...videoProps} = props;
    const {effectiveSrc, handleLoad, handleError, isLoading, hasFailed} = useMediaLoad({
        src,
        maxRetries,
    });

    const videoSrc = hasFailed && fallbackSrc ? fallbackSrc : effectiveSrc;
    const trackLoadState = Boolean(src) && !(hasFailed && fallbackSrc);

    return (
        <figure className={classNames('c-media', wrapperClassName)}>
            {isLoading && <ElementLoadingSkeleton />}
            <video
                {...videoProps}
                className={className}
                key={videoSrc || 'empty'}
                onLoadedData={trackLoadState ? handleLoad : undefined}
                onError={trackLoadState ? handleError : undefined}
                style={{
                    ...style,
                    visibility: isLoading ? 'hidden' : 'visible',
                }}
            >
                {videoSrc && <source src={videoSrc} type={type} data-testid="video-source" />}
                {unsupportedText}
            </video>
        </figure>
    );
}

export default MediaVideo;
